import { MonetizationType, TitleFilter } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { getPrimeTagSuffixByCountry } from '@/features/buybox/helpers/qtv-promotion-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { ExperimentTrackEventOptions, useExperiment } from '@/libraries/experiment';
import { computed } from 'vue';

const AVOD_BANNER_TEXT: Partial<Record<WebLocale, string>> = {
	[WebLocale.us]: 'Watch similar titles with JustWatchTV for free!',
	[WebLocale.it]: 'Guarda titoli simili con JustWatchTV gratis!',
	[WebLocale.es]: '¡Mira títulos similares con JustWatchTV gratis!',
	[WebLocale.tr]: 'Benzer içerikleri JustWatchTV ile ücretsiz izle!',
	[WebLocale.fr]: 'Regarde des titres similaires avec JustWatchTV gratuitement !',
	[WebLocale.de]: 'Streame ähnliche Titel kostenlos auf JustWatchTV!',
};

export function useJWTVExp() {
	const {
		activeVariant: jwTVTvodActiveVariant,
		experimentInfo,
		trackEvent: trackTvodExp,
	} = useExperiment('EXP_JWTV_TVOD');
	const {
		activeVariant: jwTVAvodActiveVariant,
		trackEvent: trackAvodExp,
		activeVariantWithControlGroup: jwTVAvodActiveVariantWithControlGroup,
	} = useExperiment('EXP_JWTV_AVOD');
	const { webLocale } = useLanguageStore();

	const isJWTVTvodExpActive = computed(() => {
		return !!jwTVTvodActiveVariant.value;
	});

	const isUserAcquired = computed(
		() => !!experimentInfo.value?.activated && experimentInfo.value.variant !== 'control'
	);

	type VariantKey = NonNullable<typeof jwTVTvodActiveVariant.value>;
	const expCodeByVariant: Record<VariantKey, { queryParam: string; promoCode: string }> = {
		variant_1: {
			queryParam: 'v1',
			promoCode: 'JW20',
		},
		variant_2: {
			queryParam: 'v2',
			promoCode: 'JWTV20',
		},
	};

	const userExpCode = computed(() => {
		if (isUserAcquired.value) {
			return expCodeByVariant[experimentInfo.value.variant as VariantKey];
		}
		return null;
	});

	const jwtvTextOverride = computed(() => {
		if (jwTVTvodActiveVariant.value) {
			return {
				bannerText: 'Watch similar titles that are highly rated for 20% off!',
				ctaText: 'Watch Now',
			};
		}

		if (jwTVAvodActiveVariant.value) {
			return {
				bannerText: getAvodBannerText(webLocale.value),
			};
		}

		return {};
	});

	const jwtvExpActiveVariant = computed(() => {
		if (jwTVTvodActiveVariant.value) return jwTVTvodActiveVariant.value;
		if (jwTVAvodActiveVariant.value) return jwTVAvodActiveVariant.value;
		return null;
	});

	const jwTvExpActiveFilter = computed<TitleFilter | null>(() => {
		if (jwTVTvodActiveVariant.value)
			return {
				monetizationTypes: [MonetizationType.Rent],
			};
		if (jwTVAvodActiveVariant.value)
			return {
				monetizationTypes: [MonetizationType.Free],
				imdbVotes: {
					min: 1000,
				},
				imdbScore: {
					min: 7,
				},
			};
		return null;
	});

	function trackEvent(payload: ExperimentTrackEventOptions<'EXP_JWTV_AVOD' | 'EXP_JWTV_TVOD'>) {
		trackTvodExp(payload);
		trackAvodExp(payload);
	}

	function getAvodBannerText(locale: WebLocale): string {
		return (AVOD_BANNER_TEXT[locale] || AVOD_BANNER_TEXT[WebLocale.us]) as string;
	}

	function setJwtvAvodTags(url: ClickoutUrl, country: string, shortName?: string) {
		if (
			jwTVAvodActiveVariantWithControlGroup.value !== 'control' ||
			!shortName ||
			!['atp', 'amp', 'prv'].includes(shortName)
		) {
			return url;
		}

		const variant = 'ct';
		const countryPrimeSuffix = getPrimeTagSuffixByCountry(country);
		const suffix = ['amp', 'prv'].includes(shortName) && countryPrimeSuffix ? '-' + countryPrimeSuffix : '';
		const shortId = 'jwtavod';

		const tag = ['exp', shortId, variant, country.toLowerCase(), 'ft'].join('-') + suffix;

		if (['DE', 'IT', 'FR', 'ES', 'GB'].includes(country) && ['amp', 'prv'].includes(shortName)) {
			// DE IT FR ES UK is using Prime, which is currently UCT1
			url.skipYieldOptimization().skipUCT2().setOnOffer('tag', tag);
		} else {
			url.set('uct_ctag', tag);
		}

		return url;
	}

	return {
		isJWTVTvodExpActive,
		isUserAcquired,
		userExpCode,
		trackEvent,
		jwtvTextOverride,
		jwtvExpActiveVariant,
		jwTvExpActiveFilter,
		jwTVAvodActiveVariant,
		jwTVTvodActiveVariant,
		setJwtvAvodTags,
	};
}
